import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Banner from "../Components/Shared/Layout/Banner";
import IntroBlock from "../Components/Shared/Layout/IntroBlock";
import withLanguage from "../HOC/withLanguage";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";

import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

function StudentDual({ language }) {
    const lang = useSelector((state) => state.language.currentLanguage);

    const [textItems, setTextItems] = useState([]);
    const [imgItems, setImgItems] = useState([]);

    const [loadingText, setLoadingText] = useState(true);
    const [loadingImgs, setLoadingImgs] = useState(true);
    const [errText, setErrText] = useState(null);
    const [errImgs, setErrImgs] = useState(null);

    const [openIndex, setOpenIndex] = useState(null); // modal for read more
    const truncateAt = 120;

    useEffect(() => {
        const ac = new AbortController();
        setLoadingText(true);
        setErrText(null);

        const url = `/api/dual_system/student_dual_system/?lang=${encodeURIComponent(
            lang || "sq"
        )}`;

        fetch(url, { signal: ac.signal })
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                const list = Array.isArray(data?.records) ? data.records : [];
                // Only 1–3 as per your earlier pattern
                setTextItems(list.slice(0, 3));
            })
            .catch((e) => {
                if (e.name !== "AbortError") setErrText(e.message || "Failed to load");
            })
            .finally(() => setLoadingText(false));

        return () => ac.abort();
    }, [lang]);

    useEffect(() => {
        const ac = new AbortController();
        setLoadingImgs(true);
        setErrImgs(null);

        const url = `/api/student_dual_images/student_dual_images`;
        fetch(url, { signal: ac.signal })
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                const list = Array.isArray(data?.records) ? data.records : [];
                setImgItems(list);
            })
            .catch((e) => {
                if (e.name !== "AbortError") setErrImgs(e.message || "Failed to load");
            })
            .finally(() => setLoadingImgs(false));

        return () => ac.abort();
    }, []);

    const truncate = (str, max = truncateAt) =>
        typeof str === "string" && str.length > max ? str.slice(0, max) + "..." : str ?? "";

    // carousel config: 3 desktop / 2 tablet / 1 mobile (as requested)
    const imgResponsive = {
        desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
        tablet: { breakpoint: { max: 1024, min: 640 }, items: 2 },
        mobile: { breakpoint: { max: 640, min: 0 }, items: 1 },
    };

    return (
        <>
            <Banner img="/howToApplyImages/banner.jpg" title={language?.banner} />
            <IntroBlock>{language.p1}</IntroBlock>

            {/* TEXT GRID (2 cols) */}
            {loadingText && <div className="p-6 text-center">Loading…</div>}
            {errText && <div className="p-6 text-center text-red-600">Error: {errText}</div>}

            {!loadingText && !errText && (
                <div className="m-5 xl:mx-[15rem] lg:mx-[7rem] md:mx-[3rem] xs:mx-[2.5rem]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {textItems.map((item, i) => {
                            const needsMore = (item?.description?.length || 0) > truncateAt;
                            return (
                                <div key={item.id ?? i} className="space-y-2 text-center">
                                    {/* Red title (same look) */}
                                    <h3 className="text-red-600 font-bold">{item?.title || ""}</h3>

                                    {/* White rounded card with shadow + body */}
                                    <div className="rounded-xl shadow-md bg-white p-6 text-left">
                                        {item?.description && (
                                            <p className="text-[14px] text-gray-700">
                                                {truncate(item.description, truncateAt)}{" "}
                                                {needsMore && (
                                                    <button
                                                        onClick={() => setOpenIndex(i)}
                                                        className="text-blue-600 underline cursor-pointer select-none"
                                                        aria-haspopup="dialog"
                                                        aria-controls={`student-dual-modal-${i}`}
                                                    >
                                                        {language["read_more"] || "Read more"}
                                                    </button>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* IMAGES CAROUSEL (3 cols on desktop) */}
            {loadingImgs && <div className="p-6 text-center">Loading images…</div>}
            {errImgs && <div className="p-6 text-center text-red-600">Error: {errImgs}</div>}

            {!loadingImgs && !errImgs && imgItems.length > 0 && (
                <div className="m-5 xl:mx-[15rem] lg:mx-[7rem] md:mx-[3rem] xs:mx-[2.5rem]">
                    <Carousel responsive={imgResponsive}>
                        {imgItems.map((img, idx) => {
                            const src = `data:image/jpeg;base64,${img?.image || ""}`;
                            return (
                                <div key={img.id ?? idx} className="px-2 pb-5">
                                    <div className="rounded-xl shadow-md bg-white overflow-hidden">
                                        {/* Use a fixed aspect box for neatness */}
                                        <div className="w-full aspect-[4/3]">
                                            <img
                                                src={src}
                                                alt={img?.title || `Image ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                        {img?.title && (
                                            <div className="p-3">
                                                <p className="text-center text-sm font-medium">{img.title}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Carousel>
                </div>
            )}

            {/* Modal for Read more (full text) */}
            <Modal
                open={openIndex !== null}
                onClose={() => setOpenIndex(null)}
                aria-labelledby={`student-dual-modal-title-${openIndex ?? 0}`}
                aria-describedby={`student-dual-modal-desc-${openIndex ?? 0}`}
            >
                <Box
                    id={`student-dual-modal-${openIndex ?? 0}`}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 600 },
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                    }}
                >
                    <div className="flex items-center justify-between">
                        <Typography
                            id={`student-dual-modal-title-${openIndex ?? 0}`}
                            variant="h6"
                            component="h3"
                        >
                            {textItems[openIndex ?? 0]?.title || ""}
                        </Typography>
                        <IconButton
                            aria-label={language["close"] || "Close"}
                            onClick={() => setOpenIndex(null)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <Typography
                        id={`student-dual-modal-desc-${openIndex ?? 0}`}
                        sx={{ mt: 2, whiteSpace: "pre-wrap" }}
                    >
                        {textItems[openIndex ?? 0]?.description || ""}
                    </Typography>

                    <div className="flex justify-end mt-4">
                        <Button variant="contained" onClick={() => setOpenIndex(null)}>
                            {language["close"] || "Close"}
                        </Button>
                    </div>
                </Box>
            </Modal>
        </>
    );
}

export default withLanguage(StudentDual, "StudentDual");
